import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building,
  Loader2,
  AlertCircle,
  CheckCircle,
  Star,
  Zap,
  Info,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotificationPopup } from '../../contexts/NotificationContext';
import { onboardingService } from '../../services/onboardingService';
import { getTenantById } from '../../firebase/config';
import PublicNavbar from '../nav/PublicNavbar';
import PhoneInputWithCountry from './PhoneInputWithCountry';

const TENANT_ID_PATTERN = /^[a-z0-9-]{3,40}$/;

const BUSINESS_TYPE_OPTIONS = [
  'Cafe',
  'Restaurant',
  'Bakery',
  'Cloud Kitchen',
  'Food Truck',
  'Bar',
  'QSR',
  'Other',
] as const;

const BUSINESS_TYPE_WORD: Record<string, string> = {
  Cafe: 'cafe',
  Restaurant: 'restaurant',
  Bakery: 'bakery',
  'Cloud Kitchen': 'kitchen',
  'Food Truck': 'truck',
  Bar: 'bar',
  QSR: 'outlet',
  Other: 'business',
};

const TRUST_POINTS = [
  {
    icon: <Star className="w-5 h-5 text-purple-600" />,
    title: 'Trusted by 500+ Cafes',
    desc: 'Join a growing community of food entrepreneurs scaling their business.',
  },
  {
    icon: <Zap className="w-5 h-5 text-purple-600" />,
    title: 'Instant Workspace Setup',
    desc: 'Get your POS ready in seconds. No complex migrations or downtime.',
  },
  {
    icon: <CheckCircle className="w-5 h-5 text-purple-600" />,
    title: 'Free Plan Included',
    desc: 'Start with our comprehensive free tier and upgrade whenever you are ready.',
  },
];

const slugify = (text: string) =>
  text
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-');

const CreateBusinessPage: React.FC = () => {
  const { currentUser, refreshTenantData } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotificationPopup();

  const [businessName, setBusinessName] = useState('');
  const [businessId, setBusinessId] = useState('');
  const [isIdManual, setIsIdManual] = useState(false);
  const [businessType, setBusinessType] = useState<string>(BUSINESS_TYPE_OPTIONS[0]);
  const [contactPerson, setContactPerson] = useState('');
  const [contactEmail, setContactEmail] = useState(currentUser?.email || '');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('India');

  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [typeWord, setTypeWord] = useState('cafe');
  const [wordVisible, setWordVisible] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (currentUser?.email) {
      setContactEmail(currentUser.email);
    }
  }, [currentUser?.email]);

  useEffect(() => {
    const newWord = BUSINESS_TYPE_WORD[businessType] ?? 'business';
    setWordVisible(false);
    const t = setTimeout(() => {
      setTypeWord(newWord);
      setWordVisible(true);
    }, 200);
    return () => clearTimeout(t);
  }, [businessType]);

  useEffect(() => {
    if (!isIdManual && businessName) {
      setBusinessId(slugify(businessName));
      setIsAvailable(null);
    }
  }, [businessName, isIdManual]);

  const checkBusinessIdAvailability = useCallback(async (idToCheck: string) => {
    if (!idToCheck || !TENANT_ID_PATTERN.test(idToCheck)) {
      setIsAvailable(null);
      return;
    }
    setCheckingAvailability(true);
    try {
      const existing = await getTenantById(idToCheck);
      setIsAvailable(!existing);
    } catch {
      setIsAvailable(null);
    } finally {
      setCheckingAvailability(false);
    }
  }, []);

  const canSubmit = useMemo(() => {
    return (
      businessName.trim().length > 0 &&
      TENANT_ID_PATTERN.test(businessId) &&
      contactPerson.trim().length > 0 &&
      contactEmail.trim().length > 0 &&
      phone.trim().length > 0 &&
      address.trim().length > 0 &&
      isAvailable !== false &&
      !loading
    );
  }, [businessName, businessId, contactPerson, contactEmail, phone, address, isAvailable, loading]);

  const handleBusinessNameBlur = () => {
    const trimmed = businessName.trim();
    if (trimmed !== businessName) setBusinessName(trimmed);
    const idForCheck = isIdManual ? businessId : slugify(trimmed);
    if (!isIdManual) setBusinessId(idForCheck);
    if (idForCheck.length >= 3) void checkBusinessIdAvailability(idForCheck);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    if (isAvailable === false) {
      setError('This Business ID is already taken. Please choose another one.');
      return;
    }

    const nameParts = contactPerson.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    try {
      setError('');
      setLoading(true);

      await onboardingService.createBusiness({
        businessName: businessName.trim(),
        tenantId: businessId.trim(),
        firstName,
        lastName,
        phone: phone.trim(),
        type: businessType,
        contactPerson: contactPerson.trim(),
        email: contactEmail.trim(),
        address: address.trim(),
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        country: country.trim() || undefined,
        workspacePlan: 'free',
      });

      await currentUser.getIdToken(true);
      await refreshTenantData();

      showNotification({
        id: `business-created-${Date.now()}`,
        type: 'success',
        title: 'Business created!',
        message: 'Your workspace is ready. Welcome to CafeLawn.',
        priority: 'medium',
        isRead: false,
      });

      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Failed to create your business workspace. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PublicNavbar />
      <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 md:pt-12 pb-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
            {/* Left panel */}
            <div className="md:col-span-5 space-y-8">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-widest text-purple-600 mb-2">
                  Get Started
                </p>
                <h1 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-white leading-tight mb-4">
                  Register your{' '}
                  <span
                    className="text-purple-600 inline-block transition-all duration-200"
                    style={{
                      opacity: wordVisible ? 1 : 0,
                      transform: wordVisible ? 'translateY(0)' : 'translateY(6px)',
                    }}
                  >
                    {typeWord}
                  </span>{' '}
                  in minutes
                </h1>
                <p className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed">
                  Simplify your cafe operations, manage orders and inventory, and grow your revenue.
                </p>
              </div>

              <div className="space-y-5">
                {TRUST_POINTS.map((point) => (
                  <div key={point.title} className="flex items-start gap-3">
                    <div className="p-2.5 rounded-full bg-purple-100 dark:bg-purple-900/30 flex-shrink-0">
                      {point.icon}
                    </div>
                    <div>
                      <h3 className="font-extrabold text-gray-900 dark:text-white">{point.title}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{point.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Your business will be initialized on the <strong>Free Plan</strong>. Upgrade anytime
                  from your dashboard.
                </p>
              </div>
            </div>

            {/* Right panel — form */}
            <div className="md:col-span-7">
              <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 sm:p-8 shadow-sm">
                {/* Free plan banner */}
                <div className="mb-6 p-4 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs font-extrabold uppercase text-purple-600">Current Selection</p>
                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                        Workspace on <strong>Free Plan</strong>
                      </p>
                    </div>
                    <Link
                      to="/pricing"
                      className="text-sm font-bold text-purple-600 hover:text-purple-500 whitespace-nowrap"
                    >
                      Change Plan
                    </Link>
                  </div>
                </div>

                {error && (
                  <div className="mb-5 flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      onBlur={handleBusinessNameBlur}
                      placeholder="e.g. Sunrise Cafe"
                      required
                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Business ID *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={businessId}
                        onChange={(e) => {
                          setIsIdManual(true);
                          setIsAvailable(null);
                          setBusinessId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                        }}
                        onBlur={(e) => {
                          const normalized = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                          setBusinessId(normalized);
                          void checkBusinessIdAvailability(normalized);
                        }}
                        required
                        className={`w-full px-3 py-2.5 pr-36 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 ${
                          isAvailable === false
                            ? 'border-red-400'
                            : isAvailable === true
                            ? 'border-green-400'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                        <span className="text-sm text-gray-400">@cafelawn.com</span>
                        {checkingAvailability && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                        {!checkingAvailability && isAvailable === true && (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        )}
                        {!checkingAvailability && isAvailable === false && (
                          <AlertCircle className="w-4 h-4 text-red-500" />
                        )}
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {isAvailable === false
                        ? 'This ID is already taken'
                        : isAvailable === true
                        ? 'This ID is available'
                        : 'A unique identifier for your workspace (3–40 chars, lowercase, numbers, hyphens)'}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Business Type *
                      </label>
                      <select
                        value={businessType}
                        onChange={(e) => setBusinessType(e.target.value)}
                        required
                        className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                      >
                        {BUSINESS_TYPE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Contact Person *
                      </label>
                      <input
                        type="text"
                        value={contactPerson}
                        onChange={(e) => setContactPerson(e.target.value)}
                        required
                        className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Contact Email *
                      </label>
                      <input
                        type="email"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        required
                        className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Contact Number *
                      </label>
                      <PhoneInputWithCountry value={phone} onChange={setPhone} />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Address *
                    </label>
                    <input
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      required
                      placeholder="Street address"
                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City</label>
                      <input
                        type="text"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">State</label>
                      <input
                        type="text"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Country</label>
                      <input
                        type="text"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                  <button
                    type="button"
                    onClick={() => navigate('/pricing')}
                    className="px-6 py-2.5 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Building className="w-4 h-4" />
                        Create Business
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateBusinessPage;
